import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import MicNoneIcon from '@mui/icons-material/MicNone';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { SHOW_WHATS_NEW_EVENT } from './WhatsNewModal';
import PageHeader from './common/PageHeader';

function getFirstName(user) {
  const name = user?.name || user?.username || '';
  const parts = name.trim().split(/\s+/);
  return parts[0] || '';
}

function PrimaryCard({ onClick }) {
  const theme = useTheme();
  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        cursor: 'pointer',
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.08,
        )} 0%, ${alpha(theme.palette.primary.main, 0.18)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, md: 3 },
        textAlign: 'left',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.18)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <MicNoneIcon sx={{ fontSize: 32 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.3 }}
        >
          Neue Transkription starten
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ fontSize: '0.95rem', mt: 0.5 }}
        >
          Lade eine Audio- oder Videodatei hoch — wir übernehmen den Rest.
        </Typography>
      </Box>
      <ArrowForwardIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
    </Paper>
  );
}

function ActionCard({ title, description, icon, onClick }) {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        p: 2.5,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        height: '100%',
        // Ohne border-box kommt der Innenabstand zu den 100% Höhe hinzu, die
        // Kachel ragt über ihre Grid-Zelle und überdeckt bei schmalem Fenster
        // die nächste.
        boxSizing: 'border-box',
        textAlign: 'left',
        transition:
          'border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }}
          noWrap
        >
          {title}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{
            fontSize: '0.85rem',
            mt: 0.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {description}
        </Typography>
      </Box>
    </Paper>
  );
}

function UserView() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const greetName = getFirstName(accounts?.[0]);

  const showWhatsNew = () => {
    window.dispatchEvent(new CustomEvent(SHOW_WHATS_NEW_EVENT));
  };

  const secondaryCards = [
    {
      title: 'Meine Transkriptionen',
      description: 'Alle deine bisherigen Jobs und Ergebnisse.',
      icon: <LibraryBooksOutlinedIcon />,
      to: '/transcriptions',
    },
    {
      title: '1-Click KI-Funktionen',
      description:
        'Eigene Vorlagen für Protokolle, Zusammenfassungen oder Aufgabenlisten anlegen.',
      icon: <BoltOutlinedIcon />,
      to: '/settings/oneclick',
    },
  ];

  return (
    <Box sx={{ maxWidth: 840, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <PageHeader
        centered
        overline="Transcribe"
        title={greetName ? `Willkommen, ${greetName}` : 'Willkommen zurück'}
        subtitle="Transkribiere Audio- und Videodateien und arbeite direkt mit den Inhalten per KI."
      />

      <PrimaryCard onClick={() => navigate('/transcribe')} />

      <Grid container spacing={2} mt={0.5}>
        {secondaryCards.map((c) => (
          <Grid item xs={12} sm={6} key={c.to}>
            <ActionCard
              title={c.title}
              description={c.description}
              icon={c.icon}
              onClick={() => navigate(c.to)}
            />
          </Grid>
        ))}
      </Grid>

      <Box textAlign="center" mt={{ xs: 4, md: 5 }}>
        <Button
          onClick={showWhatsNew}
          size="small"
          color="inherit"
          startIcon={<CampaignOutlinedIcon />}
          sx={{ color: 'text.secondary', textTransform: 'none' }}
        >
          Was ist neu?
        </Button>
      </Box>
    </Box>
  );
}

export default UserView;
